import { bytesToBase64, hashString } from '../lib/utils';

/**
 * Low-level Office JS wrappers for reading/writing the active Word document.
 * All functions must be called after Office.onReady() has resolved.
 */

/**
 * Read the current Word document as DOCX bytes. Uses the Common API's
 * getFileAsync, which returns the document in slices (max 4 MB each).
 * We concatenate them into a single Uint8Array.
 *
 * Throws on failure (network, permissions, closed document).
 */
export async function readCurrentDocumentBytes(): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 4 * 1024 * 1024 },
      async (asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(asyncResult.error?.message ?? 'getFileAsync failed'));
          return;
        }
        const file = asyncResult.value;
        try {
          const slices: Uint8Array[] = [];
          for (let i = 0; i < file.sliceCount; i++) {
            const slice = await new Promise<Uint8Array>((res, rej) => {
              file.getSliceAsync(i, (r) => {
                if (r.status !== Office.AsyncResultStatus.Succeeded) {
                  rej(new Error(r.error?.message ?? 'getSliceAsync failed'));
                  return;
                }
                const data = r.value.data as unknown;
                if (data instanceof Uint8Array) {
                  res(data);
                } else if (Array.isArray(data)) {
                  res(new Uint8Array(data as number[]));
                } else if (data instanceof ArrayBuffer) {
                  res(new Uint8Array(data));
                } else {
                  rej(new Error('Unexpected slice data type'));
                }
              });
            });
            slices.push(slice);
          }
          const total = slices.reduce((s, a) => s + a.length, 0);
          const merged = new Uint8Array(total);
          let offset = 0;
          for (const s of slices) {
            merged.set(s, offset);
            offset += s.length;
          }
          resolve(merged);
        } catch (err) {
          reject(err);
        } finally {
          file.closeAsync();
        }
      },
    );
  });
}

/**
 * Return the user's currently-selected text in Word, trimmed.
 * Returns empty string if no selection.
 */
export async function getSelectedText(): Promise<string> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.load('text');
    await context.sync();
    return selection.text?.trim() ?? '';
  });
}

/**
 * Insert HTML at the current cursor / selection in Word.
 * Used for the Research "Insert" button on citations and AI answers.
 */
export async function insertHtmlAtCursor(html: string): Promise<void> {
  await Word.run(async (context) => {
    context.document.getSelection().insertHtml(html, Word.InsertLocation.after);
    await context.sync();
  });
}

/**
 * Compute a lightweight hash of the document's plaintext + paragraph count.
 * Used to detect whether the user edited the document locally between AI
 * edit requests — if yes, we re-upload; if no, we reuse the last S3 key.
 */
export async function computeDocumentHash(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load('text');
    const paragraphs = body.paragraphs;
    paragraphs.load('items/text');
    await context.sync();
    return `p${paragraphs.items.length}:${hashString(body.text ?? '')}`;
  });
}

/**
 * Save the currently-open document. Used before launching a new Word window
 * with a different draft so pending changes aren't lost. If the document has
 * never been saved to disk (title is still "DocumentN"), Word automatically
 * shows a Save As dialog — we wait for it to resolve.
 */
export async function saveDocument(): Promise<void> {
  return Word.run(async (context) => {
    context.document.save();
    await context.sync();
  });
}

/** The Office.js change-tracking property accepts both the enum and the raw
 *  string literals, so we mirror that union for round-tripping. */
export type TrackingMode =
  | Word.ChangeTrackingMode
  | 'Off'
  | 'TrackAll'
  | 'TrackMineOnly';

/** Read the user's current change-tracking mode (Off / TrackAll / TrackMineOnly). */
export async function readChangeTrackingMode(): Promise<TrackingMode> {
  return Word.run(async (context) => {
    const doc = context.document;
    doc.load('changeTrackingMode');
    await context.sync();
    return doc.changeTrackingMode;
  });
}

/** Set the change-tracking mode. */
export async function setChangeTrackingMode(mode: TrackingMode): Promise<void> {
  await Word.run(async (context) => {
    context.document.changeTrackingMode = mode;
    await context.sync();
  });
}

/**
 * Fallback path — replace the entire document with the returned DOCX.
 * Used when OOXML surgical diffing fails (see ooxmlRedline.ts).
 *
 * IMPORTANT: the caller (applyEditedDocument) MUST have change tracking turned
 * OFF before calling this. The server DOCX already carries `w:ins`/`w:del`
 * revisions authored "NyayAssist AI"; inserting it while tracking is ON (e.g.
 * the user is in Reviewing mode) makes Word re-wrap the ENTIRE inserted body as
 * one fresh insertion by the current user — turning the whole document blue and
 * silently dropping the embedded deletions. This function therefore does NOT
 * touch changeTrackingMode itself.
 *
 * NOTE: destroys Word's undo stack. Mitigated by our local snapshot-based
 * revert UX in documentSettings.ts.
 */
export async function replaceDocumentBytes(bytes: ArrayBuffer): Promise<void> {
  const base64 = bytesToBase64(new Uint8Array(bytes));
  await Word.run(async (context) => {
    context.document.body.insertFileFromBase64(base64, Word.InsertLocation.replace);
    await context.sync();
  });
}

/**
 * Apply a "quote edit" as a native Word tracked change.
 *
 * The web app renders quote_edit responses as `<del>/<ins>` pairs inside
 * TipTap using its revision marks. In Word, the equivalent is Word's own
 * tracked changes: we force `trackAll` then replace the original text
 * with the edited text — Word records the delta as a native revision the
 * user can Accept/Reject via the Review ribbon.
 *
 * Resolution order for the range to replace:
 *   1. If `originalText` is provided, search the document for it (case-sensitive,
 *      exact). This is robust to the user having clicked elsewhere between send
 *      and response.
 *   2. Otherwise fall back to the current selection.
 *
 * If `originalText` is not found (user deleted/modified it while AI was
 * thinking), we insert the edited text at the cursor and surface the fact
 * by returning `{ matched: false }`.
 */
export interface QuoteEditApplyResult {
  matched: boolean;
}

export async function applyQuoteEdit(args: {
  editedText: string;
  originalText?: string;
}): Promise<QuoteEditApplyResult> {
  const { editedText, originalText } = args;
  return Word.run(async (context) => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

    if (originalText && originalText.trim().length > 0) {
      const results = context.document.body.search(originalText, {
        matchCase: true,
        matchWholeWord: false,
      });
      results.load('items');
      await context.sync();
      if (results.items.length > 0) {
        const first = results.items[0];
        first.insertText(editedText, Word.InsertLocation.replace);
        await context.sync();
        return { matched: true };
      }
    }

    // Fallback: replace current selection (or insert at cursor if empty).
    const selection = context.document.getSelection();
    selection.insertText(editedText, Word.InsertLocation.replace);
    await context.sync();
    return { matched: false };
  });
}
